import { type FundingSourceDTO } from '@/domain/fundingSource/types';
import { type OKR } from '@/domain/okrs/type';
import { type PortfolioWithProductsDTO } from '@/domain/productPortfolio/types';
import { type ProductBasic } from '@/domain/products/types';
import {
  type GroupedBasic,
  type PlatformVersionDTO,
  type RoadmapFundingSourceList,
  type RoadmapList,
  type RoadmapListDetail,
  type RoadmapPlatform,
  type RoadmapStatus,
} from '@/domain/roadmap/types/roadmap.interfaces';
import { type RoadMapType } from '@/domain/roadmapType/types';
import { type BasicUnit, type Unit } from '@/domain/units/types';
import { type UserBrief } from '@/domain/user/types';
import { type ReleaseFilterOption, type RoadmapItemStatus } from '@/features/roadmap/types';
import { type TreeSelectSelectionKeysType } from 'primereact/treeselect';

import {
  type AppliedFilters,
  type FilterUserItem,
  type RoadmapFilters,
  type RoadmapQueryParams,
} from '../store/filter.types';
import { type TempoFilters } from '../types/filterTypes';

export const filterByIndustry = (
  roadMap: RoadmapList | RoadmapFundingSourceList,
  selectedIndustries: TreeSelectSelectionKeysType | null,
) => {
  if (!selectedIndustries || Object.keys(selectedIndustries).length === 0) return true;
  return Object.keys(selectedIndustries).some((key) => {
    const id = key.split('-')[0];
    if (id.includes('a')) {
      return roadMap.product.industries?.some((i) => i.idIndustryArea?.toString() === id.replace('a', ''));
    }
    return roadMap.product.industries?.some((i) => i.idIndustry.toString() === id);
  });
};

export const filterByPlatform = (
  roadMap: RoadmapList | RoadmapFundingSourceList,
  selectedPlatform: RoadmapPlatform[],
) => {
  if (!selectedPlatform?.length) return true;
  return selectedPlatform.some((p) => roadMap.product.idPlatform === p.idPlatform);
};

export const filterByRelease = (
  roadMap: RoadmapList | RoadmapFundingSourceList,
  selectedRelease: GroupedBasic[],
  versions: PlatformVersionDTO[],
  releaseOption: ReleaseFilterOption,
) => {
  if (!selectedRelease.length) return true;

  const allIds = selectedRelease.flatMap((i) => i.selectedIds ?? []);

  if (releaseOption === 'Specific') {
    return roadMap.version?.idPlatformRelease && allIds.includes(roadMap.version.idPlatformRelease);
  }

  const filteredVersions = versions.filter((v) => v.idPlatformRelease && allIds.includes(v.idPlatformRelease));
  if (!filteredVersions.length) return false;
  const version = filteredVersions.reduce(
    (prev, curr) => (prev.releaseDate < curr.releaseDate ? prev : curr),
    filteredVersions[0],
  );

  return roadMap.version?.releaseDate && roadMap.version.releaseDate >= version.releaseDate;
};

export const filterByVersion = (roadMap: RoadmapList | RoadmapFundingSourceList, selectedVersion: GroupedBasic[]) => {
  if (!selectedVersion?.length) return true;
  const allIds = selectedVersion.flatMap((i) => i.selectedIds ?? []);
  return roadMap.version?.idPlatformVersion && allIds.includes(roadMap.version.idPlatformVersion);
};

const filterByOrigin = (roadMap: RoadmapList | RoadmapFundingSourceList, origin: number[]) =>
  !origin?.length || (roadMap.origin !== undefined && origin.includes(roadMap.origin));

export const filterByType = (
  roadMap: RoadmapList | RoadmapFundingSourceList,
  selectedType: RoadMapType[],
  origin: number[],
) =>
  selectedType.length === 0 ||
  selectedType.some((el) => roadMap.type?.idRoadmapType === el.idRoadmapType && filterByOrigin(roadMap, origin));

export const filterByDevelopmentTypeOnly = (
  roadMap: RoadmapList | RoadmapFundingSourceList,
  showDevelopmentItemsOnly: boolean,
) => !showDevelopmentItemsOnly || roadMap.type?.isDevelopment;

export const filterByPortfolio = (
  roadMap: RoadmapList | RoadmapFundingSourceList,
  selectedPortfolios: PortfolioWithProductsDTO[],
) =>
  !selectedPortfolios.length ||
  selectedPortfolios.some((el) => el.products.some((p) => p.idProduct === roadMap.product.idProduct));

export const filterByProduct = (roadMap: RoadmapList | RoadmapFundingSourceList, selectedProducts: ProductBasic[]) =>
  !selectedProducts.length || selectedProducts.some((p) => p.idProduct === roadMap.product.idProduct);

export const filterUserItems = (
  roadmap: RoadmapListDetail | RoadmapFundingSourceList,
  userItems: FilterUserItem[],
  idUser: number | undefined,
) => {
  if (!userItems.length) return true;
  return userItems.some((item) => {
    if (item === 'Follower') return roadmap.isFollower;
    if (item === 'Participant') return roadmap.isParticipant;
    if (item === 'Owner')
      return (
        (roadmap.idOwner ? roadmap.idOwner === idUser : roadmap.product.productOwner?.idUser === idUser) ||
        roadmap.createdById === idUser
      );
    if (item === 'Reporter') return roadmap.idReporter === idUser;
    return false;
  });
};

export const filterParticipant = (roadmap: RoadmapList | RoadmapFundingSourceList, isParticipant: boolean) =>
  !isParticipant || roadmap.isParticipant;

export const filterByOKRs = (roadMap: RoadmapList | RoadmapFundingSourceList, selectedOKRs: OKR[]) =>
  !selectedOKRs.length || roadMap.okrs?.some((el) => selectedOKRs.some((f) => f.id === el.id));

export const filterByStatus = (roadMap: RoadmapList | RoadmapFundingSourceList, selectedStatus: RoadmapStatus[]) =>
  !selectedStatus.length || selectedStatus.some((el) => roadMap.status?.idRoadmapStatus === el.idRoadmapStatus);

export const filterByProductOwners = (
  roadMap: RoadmapList | RoadmapFundingSourceList,
  selectedProductOwners: UserBrief[],
) =>
  !selectedProductOwners.length ||
  selectedProductOwners.some((user) => user.idUser === roadMap.product.productOwner?.idUser);

export const filterByProductManagers = (
  roadMap: RoadmapList | RoadmapFundingSourceList,
  selectedProductManagers: UserBrief[],
) =>
  !selectedProductManagers.length ||
  selectedProductManagers.some((user) => user.idUser === roadMap.product.idProductManager);

export const filterByTechManagers = (
  roadMap: RoadmapList | RoadmapFundingSourceList,
  selectedTechOwners: UserBrief[],
) =>
  !selectedTechOwners.length || selectedTechOwners.some((user) => user.idUser === roadMap.product.idTechnologyManager);

export const filterByIndustryManagers = (
  roadMap: RoadmapList | RoadmapFundingSourceList,
  selectedIndustryManagers: UserBrief[],
) =>
  !selectedIndustryManagers.length ||
  selectedIndustryManagers.some((user) => roadMap.product.industries?.some((i) => i.idManager === user.idUser));

export const filterByResourceGroup = (roadMap: RoadmapList | RoadmapFundingSourceList, resourceGroups: number[]) =>
  !resourceGroups?.length || resourceGroups.some((id) => roadMap.resourceGroupIds?.includes(id));

export const filterByResource = (roadMap: RoadmapList | RoadmapFundingSourceList, resources: number[]) =>
  !resources?.length || resources.some((id) => roadMap.resourceIds?.includes(id));

export const filterByProductUnit = (roadMap: RoadmapList | RoadmapFundingSourceList, selectedProductUnit: Unit[]) =>
  !selectedProductUnit?.length || selectedProductUnit.some((unit) => unit.id === roadMap.product.productUnitId);

export const filterByUnitImpacted = (
  roadMap: RoadmapList | RoadmapFundingSourceList,
  selectedUnitImpacted: BasicUnit[],
  isRequiredUnit: boolean,
) =>
  !selectedUnitImpacted?.length ||
  roadMap.unitsImpact.some(
    (u) => selectedUnitImpacted.some((unit) => unit.unitId === u.unitId) && (!isRequiredUnit || u.isRequested),
  );

export const filterByCategory = (roadMap: RoadmapList | RoadmapFundingSourceList, selectedCategories: number[]) =>
  !selectedCategories?.length || selectedCategories.includes(roadMap.category);

export const filterByPriority = (roadMap: RoadmapList | RoadmapFundingSourceList, selectedPriorities: number[]) =>
  !selectedPriorities?.length || selectedPriorities.includes(roadMap.priority);

export const filterByOwner = (roadMap: RoadmapList | RoadmapFundingSourceList, selectedOwner: UserBrief[]) => {
  if (!selectedOwner?.length) return true;
  if (roadMap.owner) return selectedOwner.some((o) => o.idUser === roadMap.owner?.idUser);
  return selectedOwner.some((o) => o.idUser === roadMap.product.productOwner?.idUser);
};

export const filterByGlobalProductOwner = (
  roadMap: RoadmapList | RoadmapFundingSourceList,
  selectedGlobalProductOwners: UserBrief[],
) => {
  if (!selectedGlobalProductOwners?.length) return true;
  return selectedGlobalProductOwners.some((o) => o.idUser === roadMap.product.productOwner?.idUser);
};
export const filterByFundingSource = (
  roadMap: RoadmapList | RoadmapFundingSourceList,
  selectedFunding: FundingSourceDTO[],
) =>
  !selectedFunding.length ||
  roadMap.fundingSources?.some((el) => selectedFunding.some((f) => f.idFundingSource === el.idFundingSource));

export const searchByText = (roadMap: RoadmapListDetail | RoadmapFundingSourceList, searchText: string) =>
  !searchText ||
  roadMap.item.toUpperCase().includes(searchText.toUpperCase()) ||
  roadMap.product.product.toUpperCase().includes(searchText.toUpperCase()) ||
  roadMap.idRoadmap.toString().includes(searchText);

function isOverdue(roadmap: RoadmapListDetail | RoadmapFundingSourceList, now: Date): boolean {
  return roadmap.percentComplete < 100 && !!roadmap.status?.endDate && new Date(roadmap.status.endDate) < now;
}

type StatusCheck = (roadmap: RoadmapListDetail | RoadmapFundingSourceList, now: Date) => boolean;

const statusChecks: Record<RoadmapItemStatus, StatusCheck> = {
  Backlog: (r) => !r.version,
  Scheduled: (r) => !!r.version && r.percentComplete === 0,
  'In Progress': (r) => !!r.version && !r.status?.isRelease && r.percentComplete > 0 && r.percentComplete < 100,
  'In Release': (r) => !!r.version && !!r.status?.isRelease && r.percentComplete < 100,
  Released: (r) => !!r.version && !!r.status?.isRelease && r.percentComplete === 100,
  Overdue: (r, now) => isOverdue(r, now),
  'Overdue (Not Released)': (r, now) => isOverdue(r, now) && !r.status?.isRelease,
};

export const filterByItemStatus = (
  roadMap: RoadmapListDetail | RoadmapFundingSourceList,
  selectedItemStatus: RoadmapItemStatus[],
) => {
  if (!selectedItemStatus.length) return true;
  const now = new Date();
  return selectedItemStatus.some((status) => statusChecks[status](roadMap, now));
};

/** Returns true if any filter field (excluding releaseOption) has a non-empty value. */
export function hasAnyFilter(filters: RoadmapFilters | TempoFilters): boolean {
  const { releaseOption, ...rest } = filters;
  return Object.values(rest).some((v) => (Array.isArray(v) ? v.length > 0 : !!v));
}

/**
 * Parses a TreeSelect industry selection into separate industry and industry area ID arrays.
 * Keys follow the convention: "123-Name" for industries, "a456-Name" for industry areas.
 */
export function parseIndustryTreeKeys(industries: TreeSelectSelectionKeysType | null): {
  industryId: number[];
  industryAreaId: number[];
} {
  const industryId: number[] = [];
  const industryAreaId: number[] = [];

  if (industries) {
    for (const [key, state] of Object.entries(industries)) {
      if (typeof state === 'object' && state !== null && 'partialChecked' in state && state.partialChecked) continue;

      if (key.startsWith('a')) {
        industryAreaId.push(Number.parseInt(key.slice(1).split('-')[0], 10));
      } else {
        industryId.push(Number.parseInt(key.split('-')[0], 10));
      }
    }
  }

  return { industryId, industryAreaId };
}

function isAppliedFilters(filters: AppliedFilters | TempoFilters): filters is AppliedFilters {
  return 'platform' in filters;
}

export function mapFiltersToParams(filters: AppliedFilters): RoadmapQueryParams;
export function mapFiltersToParams(filters: TempoFilters): RoadmapQueryParams;
export function mapFiltersToParams(filters: AppliedFilters | TempoFilters): RoadmapQueryParams {
  const { industryId, industryAreaId } = parseIndustryTreeKeys(
    isAppliedFilters(filters) ? filters.industries : filters.industryTree ?? null,
  );

  if (isAppliedFilters(filters)) {
    return {
      platformId: filters.platform.map((p) => p.idPlatform),
      releaseId: filters.releases.flatMap((r) => r.selectedIds),
      versionId: filters.version.flatMap((v) => v.selectedIds),
      roadmapTypeId: filters.types.map((t) => t.idRoadmapType),
      statusId: filters.statuses.map((s) => s.idRoadmapStatus),
      productUnitId: filters.productUnits.map((u) => u.id),
      okrId: filters.okrs.map((o) => o.id),
      productOwnerId: filters.productOwners.map((u) => u.idUser),
      globalProductOwnerId: filters.globalProductOwner.map((u) => u.idUser),
      productManagerId: filters.productManagers.map((u) => u.idUser),
      techManagerId: filters.techManagers.map((u) => u.idUser),
      industryManagerId: filters.industryManagers.map((u) => u.idUser),
      ownerId: filters.owner.map((u) => u.idUser),
      fundingSourceId: filters.fundingSources.map((f) => f.idFundingSource),
      portfolioId: filters.portfolios.map((p) => p.idProductPortfolio),
      productId: filters.products.map((p) => p.idProduct),
      itemStatuses: filters.itemStatuses,
      originId: filters.origin,
      categoryId: filters.categories,
      priorityId: filters.priorities,
      unitsImpactedId: filters.unitsImpacted.map((u) => u.unitId),
      userItems: filters.userItems ?? [],
      industryId,
      industryAreaId,
      releaseOption: filters.releaseOption,
      showDevelopmentOnly: filters.showDevelopmentItemsOnly,
      isRequiredUnit: filters.isRequiredUnit,
      resourceGroupId: filters.resourceGroupIds,
      resourcesId: filters.resourceIds,
      externalOrigin: filters.externalOrigin,
    };
  }

  return {
    platformId: filters.platformIds ?? [],
    releaseId: filters.releaseIds ?? [],
    versionId: filters.versionIds ?? [],
    roadmapTypeId: filters.roadmapTypeIds ?? [],
    statusId: filters.statusIds ?? [],
    productUnitId: filters.productUnitIds ?? [],
    okrId: filters.businessImpactIds ?? [],
    productOwnerId: filters.productOwnerIds ?? [],
    globalProductOwnerId: filters.globalProductOwnerIds ?? [],
    productManagerId: filters.productManagerIds ?? [],
    techManagerId: filters.headOfTechnologyIds ?? [],
    industryManagerId: filters.industryManagerIds ?? [],
    ownerId: filters.ownerIds ?? [],
    fundingSourceId: filters.fundingSourceIds ?? [],
    portfolioId: filters.portfolioIds ?? [],
    productId: filters.productIds ?? [],
    itemStatuses: filters.itemStatuses ?? [],
    originId: filters.originIds ?? [],
    categoryId: filters.categoryIds ?? [],
    priorityId: filters.priorityIds ?? [],
    unitsImpactedId: filters.unitsImpacted ?? [],
    userItems: filters.userItems ?? [],
    industryId,
    industryAreaId,
    releaseOption: filters.releaseOption ?? 'Specific',
    showDevelopmentOnly: filters.showDevelopmentOnly,
    isRequiredUnit: filters.isRequiredUnit ?? false,
    resourceGroupId: filters.resourceGroupIds ?? [],
    resourcesId: filters.resourcesIds ?? [],
    externalOrigin: filters.externalOrigin ?? '',
  };
}
